<template>
  <zm-dialog
    :visible.sync="visible"
    :append-to-body="true"
    aria-label="phone number select dialog"
    class="phone-number-select opc-full-zm-dialog"
    width="544px"
  >
    <!-- phone number select dialog with title -->
    <h1 slot="title" class="phone-number-select__title">
      {{ $t('billing.pbx.search_number_title') }}
      <zm-tooltip placement="bottom" tab-to-leave aria-label="learn more about select local or toll-free number">
        <p slot="content" class="phone-number-guarantee-tips">{{ $t('billing.pbx.phone_number_guarantee_tips') }}</p>
        <i class="zm-icon-warning-outline" role="button"></i>
      </zm-tooltip>
    </h1>
    <zm-tabs
      v-model="searchByPhoneType"
      type="capsule"
      class="phone-number-select__content"
    >
      <!-- local number selection -->
      <zm-tab-pane
        :label="localNumberTabTitle"
        :name="phoneNumberTypeMap.local"
        class="phone-number-select__content--local">
        <local-search
          ref="localSearch"
          :check-exist-unread-phone-numbers="checkUnreadPhoneNumbers(phoneNumberRefsKeyMap.local)"
          :goto-next-page="gotoNextPage(phoneNumberRefsKeyMap.local)" />
        <phone-number-form-list
          ref="localNumberList"
          :max-select-phone-numbers="maxSelectPhoneNumbers"
          :type="phoneNumberTypeMap.local"
          :loading="localNumber.loading"
          :port-number-count="portCount"
          :localNumberShowClear="localNumberShowClear"
          :phone-number-list="localNumber.phoneNumberList"
          :checked-phone-number-map="checkedPhoneNumberMap"
          @checkedNumberList="handleCheckedNumberList" />
      </zm-tab-pane>
      <!-- toll free number selection -->
      <zm-tab-pane
        :label="tollFreeNumberTabTitle"
        :name="phoneNumberTypeMap.tollFree"
        class="phone-number-select__content--toll-free"
      >
        <div class="phone-number-select__toll-free-tips">
          {{ tollFreeSearchTinyTips }}
          <zm-tooltip aria-label="Learn more about Toll-free numbers" :offset="55" placement="bottom-end" tab-to-leave popper-class="phone-number-select__toll-free-tips--content">
            <p slot="content">{{ $t('billing.pbx.toll_free.popover_tips') }}</p>
            <i role="button" class="zm-icon-warning-outline"></i>
          </zm-tooltip>
        </div>
        <toll-free-search
          ref="tollFreeSearch"
          :check-exist-unread-phone-numbers="checkUnreadPhoneNumbers(phoneNumberRefsKeyMap.tf)"
          :goto-next-page="gotoNextPage(phoneNumberRefsKeyMap.tf)"
        />
        <phone-number-form-list
          ref="tfNumberList"
          :max-select-phone-numbers="maxSelectPhoneNumbers"
          :type="phoneNumberTypeMap.tollFree"
          :port-number-count="portCount"
          :loading="tollFreeNumber.loading"
          :phone-number-list="tollFreeNumber.phoneNumberList"
          :checked-phone-number-map="checkedPhoneNumberMap"
          @checkedNumberList="handleCheckedNumberList" />
      </zm-tab-pane>
    </zm-tabs>
    <!-- have selected phone number list -->
    <phone-number-checked-list
      :checked-phone-number-list="checkedPhoneNumberList"
      :checked-local-number-list="checkedLocalNumberList"
      :localNumberShowClear="localNumberShowClear"
      @removeNumber="handleRemoveNumber"
    />
    <phone-select-footer slot="footer" :disabled-save="disabledSave" :on-cancel="onCancel" :on-save="onSave"/>
  </zm-dialog>
</template>

<script type="text/jsx">
import { filter, get, values, find, forEach } from 'lodash';
import LocalSearch from '@/plans/ZOOM_PHONE/component/local-search';
import PhoneNumberFormList from '@/plans/ZOOM_PHONE/component/phone-number-form-list';
import TollFreeSearch from '@/plans/ZOOM_PHONE/component/toll-free-search';
import PhoneNumberCheckedList from '@/plans/ZOOM_PHONE/component/phone-number-checked-list';
import { ConfirmComponent } from '@/components/common/service-dialog/confirm';
import { localNumberStore, tollFreeNumberStore } from '../../store';
import { maxSelectPhoneNumbers, PBX_REGIONS, phoneNumberTypeMap } from '@/plans/ZOOM_PHONE/helper/constants';
import computedMixin from '@/plans/ZOOM_PHONE/mixin/computed';
import { BILLING_CYCLE } from '@/common/constants';
import PhoneSelectFooter from "./phone-select-footer";
import { getCurrentTrackingPlanName } from '@/plans/ZOOM_PHONE/helper/utils';

export default {
  name: 'PhoneNumberSelectDialog',
  mixins: [ConfirmComponent, computedMixin],
  components: { PhoneNumberCheckedList, PhoneNumberFormList, LocalSearch, TollFreeSearch, PhoneSelectFooter },
  data() {
    return {
      searchByPhoneType: phoneNumberTypeMap.local,
      phoneNumberTypeMap,
      localNumber: localNumberStore.state,
      tollFreeNumber: tollFreeNumberStore.state,
      checkedPhoneNumberMap: {},
      // true means phone number not required check
      // false means phone number should required check
      localCount: 0,
      tollFreeCount: 0,
      portCount: 0,
      clearable: true,
      phoneNumberRefsKeyMap: Object.freeze({
        local: 'localNumberList',
        tf: 'tfNumberList'
      }),
      maxSelectPhoneNumbers: maxSelectPhoneNumbers,
    };
  },
  computed: {
    trackingPlanName() {
      return getCurrentTrackingPlanName();
    },
    checkedPhoneNumberList() {
      return values(this.checkedPhoneNumberMap) || [];
    },
    checkedLocalNumberList() {
      return filter(this.checkedPhoneNumberList, (item) => item.type === phoneNumberTypeMap.local);
    },
    checkedTollFreeNumberList() {
      return filter(this.checkedPhoneNumberList, (item) => item.type === phoneNumberTypeMap.tollFree);
    },
    localNumberTabTitle() {
      const count = this.checkedLocalNumberList?.length;
      return this.$t('billing.pbx.local.title') + `${count ? ` (${count})` : ''}`;
    },
    tollFreeNumberTabTitle() {
      const count = this.checkedTollFreeNumberList?.length;
      return this.$t('billing.pbx.toll_free.title') + `${count ? ` (${count})` : ''}`;
    },
    checkedLocalNumberCount() {
      return this.checkedLocalNumberList?.length || 0;
    },
    localNumberShowClear() {
      // allow clear means can clear all phone numbers
      return this.clearable ? true : (this.checkedLocalNumberCount === 1 ? false : true);
    },
    disabledSave() {
      // not allowed to clear phone number and phone number not checked
      // in other words, should required phone number, but phone number not selected
      return !this.clearable && !this.checkedPhoneNumberList?.length;
    },
    tollFreeSearchTinyTips() {
      const i18mKey = this.pbxBillingCycle === BILLING_CYCLE.ANNUAL ?
        'billing.pbx.toll_free.search_tips.annual' :
        'billing.pbx.toll_free.search_tips.monthly';

      const currentTollFreePlans = find(this.allTollFreePlans, (p) => {
        return p.billing_cycle === this.pbxBillingCycle && p.region === PBX_REGIONS.US_AND_CANADA;
      });

      return this.$t(i18mKey, [this.$money(get(currentTollFreePlans, 'amount'))]);
    },
  },
  methods: {
    onSave() {
      this.$tracking.buyInteraction(`${this.trackingPlanName} numbers`, `${this.callingPlanName} - pick your numbers - local and toll-free  - save`);
      return this.handleSure({ checkedPhoneNumberMap: this.checkedPhoneNumberMap });
    },
    onCancel() {
      this.$tracking.buyInteraction(`${this.trackingPlanName} numbers`, `${this.callingPlanName} - pick your numbers - local and toll-free - cancel`);
      return this.handleCancel('close select phone number dialog');
    },
    handleCheckedNumberList(val) {
      this.checkedPhoneNumberMap = val;
    },
    handleRemoveNumber(phoneNumber) {
      this.$delete(this.checkedPhoneNumberMap, phoneNumber);
    },
    resetSearchForm() {
      forEach([this.$refs?.localSearch, this.$refs?.tollFreeSearch], (formInstance) => {
        formInstance?.resetForm();
      })
    },
    checkUnreadPhoneNumbers(refKey) {
      return () => {
        return this.$refs?.[refKey]?.checkExistUnreadPhoneNumbers();
      }
    },
    gotoNextPage(refKey) {
      return () => {
        return this.$refs?.[refKey]?.gotoNextPage();
      };
    },
  },
  watch: {
    visible: {
      handler(val) {
        !val && this.resetSearchForm();
      },
    }
  }
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.phone-number-select {
  @include media(desktop, tablet) {
    min-height: 500px;
  }

  @include media(mobile) {
    overflow-y: hidden;
  }

  .zm-dialog {
    padding: 0px 0px 92px 0px;
  }

  &__toll-free-tips {
    display: flex;
    align-items: center;
    font-size: 12px;
    margin-bottom: 10px;

    &--content {
      width: 290px;
    }

    .zm-tooltip {
      color: #0B5CFF;
      font-size: 14px;
      margin-left: 5px;
      cursor: pointer;
    }
  }

  &__title, &__footer {
    padding: 0px 32px;
  }

  &__title {
    padding: 32px 4px 4px 32px;

    .phone-number-guarantee-tips {
      font-weight: normal;
    }

    .zm-icon-warning-outline {
      color: #0B5CFF;
      font-size: 14px;
      margin-left: 5px;
      cursor: pointer;
    }
  }

  &__content {
    padding: 0px 28px;

    &.zm-tabs--capsule > {
      .zm-tabs {
        &__header, &__content {
          padding: 4px;
        }

        &__header {
          margin-bottom: 12px;

          .zm-tabs__item {
            margin-right: 12px;
            border-radius: 999px;
            padding: 5px 15px;
            border: 1px solid #B5D0F8 !important;
            font-size: 16px;
            line-height: 19px;

            &.is-active {
              color: #0B5CFF;
              background: rgba(215, 230, 252, 0.5);
              border: 1px solid #0B5CFF !important;
            }
          }
        }
      }
    }

    &--local, &--toll-free {

    }

    .phone-number-list {
      &__content {

      }
    }
  }

  &__footer {
    position: absolute;
    bottom: 0px;
    height: 92px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border-top: 1px solid #E0E0E6;

    .zm-button {
      padding: 10px 24px;
    }

    .zm-button + .zm-button {
      margin-left: 16px;
    }
  }

  @include media(mobile) {
    .zm-dialog {
      &__body {
        padding-left: 12px;
        padding-right: 12px;
        scroll-behavior: smooth;
      }

      &__footer {
        height: 76px;
        padding-top: 0px;

        .phone-number-select {
          &__footer {
            height: 76px;
            padding: 12px 16px 24px;
            display: flex;
            justify-content: center;
            background: white;

            .zm-button.is-round {
              flex: 1;
            }
          }
        }
      }
    }

    &__title {
      padding-top: 0px;
    }

    &__title, &__content{
      padding-left: 0px;
      padding-right: 0px;
    }

    .checked-phone-number__wrap {
      padding-left: 4px;
      padding-right: 4px;
    }

    .phone-number-list__refresh {
      margin-top: 8px;
    }
  }

  @include media(tablet) {
    .zm-dialog {
      &__body {
        padding-bottom: 50px;
      }
    }
  }
}
</style>
